import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Filter, X, ChevronDown } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { categories } from '../data/products'
import ProductCard from '../components/product/ProductCard'
import SEO from '../components/SEO'

const sortOptions = [
  { value: 'featured', label: 'Featured' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'name', label: 'Name: A-Z' },
]

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
}

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
}

export default function Shop() {
  const { category } = useParams()
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [sortBy, setSortBy] = useState('featured')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  useEffect(() => {
    fetchProducts()
  }, [category])

  const fetchProducts = async () => {
    setIsLoading(true)
    try {
      let query = supabase
        .from('products')
        .select('*')
        .eq('in_stock', true)

      if (category) {
        query = query.eq('category', category)
      }

      const { data, error } = await query

      if (error) throw error
      setProducts(data || [])
    } catch (error) {
      console.error('Error fetching products:', error)
      setProducts([])
    } finally {
      setIsLoading(false)
    }
  }

  const currentCategory = categories.find((c) => c.id === category)

  const filteredProducts = useMemo(() => {
    let result = [...products]

    // Sort products
    switch (sortBy) {
      case 'price-asc':
        result = result.sort((a, b) => a.price - b.price)
        break
      case 'price-desc':
        result = result.sort((a, b) => b.price - a.price)
        break
      case 'name':
        result = result.sort((a, b) => a.name.localeCompare(b.name))
        break
      case 'featured':
      default:
        result = result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
        break
    }

    return result
  }, [products, sortBy])

  return (
    <div className="min-h-screen bg-pp-ivory">
      <SEO
        title="Shop"
        description="Browse our collection of premium sensory teethers. Food-grade silicone, safety tested, and designed for your baby's development."
        url="/shop"
      />
      {/* Header */}
      <section className="bg-pp-linen py-12 lg:py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-display text-display-md lg:text-display-lg text-pp-ink mb-4">
              {currentCategory ? currentCategory.name : 'All Products'}
            </h1>
            <p className="text-pp-stone max-w-lg mx-auto">
              {currentCategory
                ? currentCategory.description
                : 'Explore our complete collection of premium sensory teethers.'}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <h3 className="font-display text-lg text-pp-ink mb-4">Categories</h3>
              <nav className="space-y-2">
                <Link
                  to="/shop"
                  className={`block py-2 text-sm transition-colors ${
                    !category
                      ? 'text-pp-terracotta font-medium'
                      : 'text-pp-stone hover:text-pp-charcoal'
                  }`}
                >
                  All Products
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop/${cat.id}`}
                    className={`block py-2 text-sm transition-colors ${
                      category === cat.id
                        ? 'text-pp-terracotta font-medium'
                        : 'text-pp-stone hover:text-pp-charcoal'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-8">
              <p className="text-pp-stone text-sm">
                {isLoading ? 'Loading...' : `${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''}`}
              </p>

              <div className="flex items-center gap-4">
                {/* Mobile Filter Button */}
                <button
                  onClick={() => setMobileFiltersOpen(true)}
                  className="lg:hidden flex items-center gap-2 text-sm text-pp-charcoal"
                >
                  <Filter className="w-4 h-4" />
                  Filter
                </button>

                {/* Sort Dropdown */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-pp-sand rounded-lg px-4 py-2 pr-10 text-sm text-pp-charcoal focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pp-stone pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Product Grid */}
            {isLoading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-pp-sand/30 rounded-2xl aspect-square mb-4"></div>
                    <div className="h-4 bg-pp-sand/30 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-pp-sand/30 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length > 0 ? (
              <motion.div
                initial="initial"
                animate="animate"
                variants={staggerContainer}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
              >
                {filteredProducts.map((product) => (
                  <motion.div key={product.id} variants={fadeInUp}>
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <div className="text-center py-16">
                <p className="text-pp-stone">No products found in this category.</p>
                <Link to="/shop" className="text-pp-terracotta mt-2 inline-block">
                  View all products
                </Link>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            className="absolute right-0 top-0 bottom-0 w-80 bg-white p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-lg text-pp-ink">Filters</h3>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="p-2 text-pp-charcoal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-pp-charcoal">Categories</h4>
              <nav className="space-y-2">
                <Link
                  to="/shop"
                  onClick={() => setMobileFiltersOpen(false)}
                  className={`block py-2 text-sm ${
                    !category ? 'text-pp-terracotta font-medium' : 'text-pp-stone'
                  }`}
                >
                  All Products
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop/${cat.id}`}
                    onClick={() => setMobileFiltersOpen(false)}
                    className={`block py-2 text-sm ${
                      category === cat.id
                        ? 'text-pp-terracotta font-medium'
                        : 'text-pp-stone'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </nav>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
